
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { isEmpty } from "lodash";

import CustomDatePicker from "../../components/CustomDatePicker";
import MyAccountFilter from "../MyAccount/MyAccountFilter";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import { levelMap, toFixedWithoutRounding } from "../../Utils/Constant";

const DEFAULT_FILTER = {
  fromPeriod: moment().subtract(1, "day").format("YYYY-MM-DD"),
  toPeriod: moment().format("YYYY-MM-DD"),
  filterByDay: "",
 limit: 10,
};

const plClass = (value) => (Math.sign(value) === -1 ? "loss-text" : "win-text");
const plLabel = (value) => `(${toFixedWithoutRounding(Math.abs(value || 0))})`;

const setChildAt = (nodes, userType, userTypeId, child) =>
  nodes?.map((node) => {
    if (node?.userType === userType && node?.userTypeId === userTypeId) {
      return { ...node, child, expand: true };
    }
    if (!isEmpty(node?.child)) {
      return {
        ...node,
        child: setChildAt(node.child, userType, userTypeId, child),
      };
    }
    return node;
  });

const ReportByAWCcasino = () => {
  const tableRef = useRef();
  const [tab, setTab] = useState("casinoGame");
  const [filter, setFilter] = useState(DEFAULT_FILTER);

  // Casino Game Pnl tab
  const [gamePlRows, setGamePlRows] = useState([]);
  const [expandedGame, setExpandedGame] = useState("");
  const [gameDetails, setGameDetails] = useState([]);

  // Casino tab (hierarchical) - a Set of expanded userTypeIds, so drilling
  // into a deeper level keeps every ancestor level visible too, instead of
  // collapsing the one above it
  const [casinoRows, setCasinoRows] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Player P/L tab
  const [playerRows, setPlayerRows] = useState([]);

  const getCasinoGameData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(
      apiPath.reportAwcGamePl,
      activeFilter,
    );
    if (status === 200 && response_users.success) {
      setGamePlRows(response_users.results?.data || []);
      setExpandedGame("");
      setGameDetails([]);
    }
  };

  const getPlayerPlData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(
      apiPath.reportAwcPlayerPl,
      activeFilter,
    );
    if (status === 200 && response_users.success) {
      setPlayerRows(response_users.results?.data || []);
    }
  };

  const getCasinoData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(
      apiPath.reportAwc,
      activeFilter,
    );
    if (status === 200 && response_users.success) {
      setCasinoRows(response_users.results?.data || []);
      setExpandedIds(new Set());
    }
  };

  const getData = (activeFilter, activeTab) => {
    if (activeTab === "casinoGame") getCasinoGameData(activeFilter);
    else if (activeTab === "playerPnl") getPlayerPlData(activeFilter);
    else if (activeTab === "casino") getCasinoData(activeFilter);
  };

  useEffect(() => {
    getData(filter, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filter.limit]);

  const handleTabChange = (nextTab) => setTab(nextTab);

  const applyPreset = (preset) => {
    const nextFilter = { ...filter, filterByDay: preset };
    setFilter(nextFilter);
    getData(nextFilter, tab);
  };

  const handleLimitChange = (e) =>
    setFilter((prev) => ({ ...prev, limit: Number(e.target.value) }));

  // --- Casino Game Pnl: level-1 (sport/platform) -> level-2 (game) ---
  const toggleGameExpand = async (item) => {
    if (expandedGame === item?.runnerName) {
      setExpandedGame("");
      setGameDetails([]);
      return;
    }
    setExpandedGame(item?.runnerName);
    const { status, data: response_users } = await apiGet(
      apiPath.reportAwcGamePlList,
      {
        ...filter,
        runnerName: item?.runnerName,
      },
    );
    if (status === 200 && response_users.success) {
      setGameDetails(response_users.results?.data || []);
    }
  };

  // --- Casino tab: recursive downline expand ---
  const toggleCasinoExpand = async (item) => {
    const id = item?.userTypeId;

    if (expandedIds.has(id)) {
      // collapse - keep the fetched child data cached, just hide it
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }

    setExpandedIds((prev) => new Set(prev).add(id));

    // already fetched (even if it came back empty) - don't refetch
    if (item?.child !== undefined) return;

    const { status, data: response_users } = await apiGet(apiPath.reportAwc, {
      ...filter,
      created_by: item?.userTypeId,
      userType: item?.userType,
    });
    if (status === 200 && response_users.success) {
      setCasinoRows((prev) =>
        setChildAt(
          prev,
          item?.userType,
          item?.userTypeId,
          response_users.results?.data || [],
        ),
      );
    }
  };

  const totalRecords =
    tab === "casinoGame"
      ? gamePlRows.length
      : tab === "playerPnl"
        ? playerRows.length
        : casinoRows.length;

  const gamePlTotal = gamePlRows.reduce(
    (sum, r) => sum + Number(r?.amount || 0),
    0,
  );
  const gamePlStakeTotal = gamePlRows.reduce(
    (sum, r) => sum + Math.abs(Number(r?.amount || 0)),
    0,
  );
  const casinoTotal = casinoRows.reduce(
    (sum, r) => sum + Number(r?.amount || 0),
    0,
  );
  const playerTotal = playerRows.reduce(
    (sum, r) => sum + Number(r?.amount || 0),
    0,
  );

  const renderCasinoNode = (node, depth) => {
    const isOpen = expandedIds.has(node?.userTypeId);
    const hasNoChildren =
      isOpen && Array.isArray(node?.child) && node.child.length === 0;

    return (
      <React.Fragment key={node?.userTypeId}>
        <tr
          id="table_DL"
          className={depth > 0 ? `expand expand_${depth + 1}` : undefined}
        >
          <td className="align-L" style={{ paddingLeft: depth * 16 }}>
            {node?.userType != "user" && (
              <a
                id="_bySport"
                className={isOpen ? "expand-close" : "expand-open"}
                style={{ cursor: "pointer" }}
                onClick={() => toggleCasinoExpand(node)}
              />
            )}
            <a id="_userName" className="ico_account">
              <span className={`Bdlevel ${levelMap[node?.userType]}`}></span>{" "}
              {node?.username}{" "}
            </a>
          </td>
          <td id="_profitLoss">
            <div>
              <span className={plClass(node?.amount)}>
                {" "}
                {plLabel(node?.amount)}{" "}
              </span>
            </div>
          </td>
          <td>
            <div>
              <span className={plClass(node?.amount)}>
                {plLabel(node?.amount)}
              </span>
            </div>
          </td>
          <td id="_tax2">
            <div>
              <span> {toFixedWithoutRounding(node?.commission || 0)} </span>
            </div>
          </td>
          <td id="_profitLossUpLine">
            <div>
              <span>
                {" "}
                {toFixedWithoutRounding(Math.abs(node?.amount || 0))}{" "}
              </span>
            </div>
          </td>
        </tr>

        {isOpen &&
          node?.child?.length > 0 &&
          node.child.map((child) => renderCasinoNode(child, depth + 1))}

        {hasNoChildren && (
          <tr className={`expand expand_${depth + 2}`}>
            <td
              className="align-L"
              style={{ paddingLeft: (depth + 1) * 16 }}
              colSpan={5}
            >
              No further downline records
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <app-pl-awccasino _nghost-serverapp-c116 className="ng-star-inserted">
      <div _ngcontent-serverapp-c116 className="main_wrap">
        <div _ngcontent-serverapp-c116 className="box">
          <h2 _ngcontent-serverapp-c116>Profit/Loss Report by AWC Casino</h2>
        </div>
        <ul className="report-tab-wrap">
          <li
            id="reportType_exchange"
            data-reporttabtype={0}
            className={`report-tab ${tab === "casino" ? "select" : ""}`}
            onClick={() => handleTabChange("casino")}
          >
            <a href="#" onClick={(e) => e.preventDefault()}>
              Casino
            </a>
          </li>

          <li
            id="reportType_sportsBook"
            data-reporttabtype={2}
            className={`report-tab ${tab === "casinoGame" ? "select" : ""}`}
            onClick={() => handleTabChange("casinoGame")}
          >
            <a href="#" onClick={(e) => e.preventDefault()}>
              Casino Game Pnl
            </a>
          </li>

          <li
            className={`report-tab ${tab === "playerPnl" ? "select" : ""}`}
            tabIndex={0}
            onClick={() => handleTabChange("playerPnl")}
          >
            <a href="#" onClick={(e) => e.preventDefault()}>
              Player P/L
            </a>
          </li>
        </ul>
        <div _ngcontent-serverapp-c116 className="function-wrap">
          <ul _ngcontent-serverapp-c116 className="input-list flowroot">
            <li _ngcontent-serverapp-c116 />
            <li _ngcontent-serverapp-c116>
              <label _ngcontent-serverapp-c116>Period</label>
            </li>
            <CustomDatePicker filter={filter} setFilter={setFilter} />
          </ul>
          <ul _ngcontent-serverapp-c116 className="input-list flowroot">
            <li _ngcontent-serverapp-c116>
              <a
                _ngcontent-serverapp-c116
                id="today"
                className="btn"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("today");
                }}
              >
                Just for today
              </a>
            </li>
            <li _ngcontent-serverapp-c116>
              <a
                _ngcontent-serverapp-c116
                id="yesterday"
                className="btn"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("yesterday");
                }}
              >
                From yesterday
              </a>
            </li>
            <li _ngcontent-serverapp-c116>
              <a
                _ngcontent-serverapp-c116
                id="getPL"
                className="btn-send"
                onClick={(e) => {
                  e.preventDefault();
                  getData(filter, tab);
                }}
              >
                {" "}
                Get P &amp; L
              </a>
            </li>
          </ul>
        </div>
        <div _ngcontent-serverapp-c116 id="noReportMessage" />
        <div _ngcontent-serverapp-c116 id="userTreeDiv" className="total_all">
          <div _ngcontent-serverapp-c116 id="treePath" className="agent_path" />
          <span _ngcontent-serverapp-c116>
            <label _ngcontent-serverapp-c116>Last:</label>
            <select
              _ngcontent-serverapp-c116
              name="limit"
              id="limit"
              value={filter.limit}
              onChange={handleLimitChange}
            >
              <option _ngcontent-serverapp-c116 value={100}>
                100 Txn
              </option>
              <option _ngcontent-serverapp-c116 value={50}>
                50 Txn
              </option>
              <option _ngcontent-serverapp-c116 value={25}>
                25 Txn
              </option>
              <option _ngcontent-serverapp-c116 value={10}>
                10 Txn
              </option>
            </select>
          </span>
          <span _ngcontent-serverapp-c116 className="spacer" />
          <span
            _ngcontent-serverapp-c116
            className="total-users ng-star-inserted"
            style={{ fontWeight: "600" }}
          >
            Total Records: {totalRecords}{" "}
          </span>
          <MyAccountFilter
            type={"new"}
            tableRef={tableRef}
            fileName="report-AWC-casino"
          />
        </div>

        {tab === "casinoGame" && (
          <div
            _ngcontent-serverapp-c121
            className="over-wrap"
            style={{ maxHeight: "calc(100% - 32px - 93px - 55px)" }}
          >
            <div
              _ngcontent-serverapp-c121
              className="table-responsive data-table"
            >
              <div _ngcontent-serverapp-c121 id="report">
                <table
                  ref={tableRef}
                  _ngcontent-serverapp-c121
                  id="table_DL"
                  className="table-s"
                  style={{ display: "table" }}
                >
                  <tbody _ngcontent-serverapp-c121>
                    <tr _ngcontent-serverapp-c121>
                      <th
                        _ngcontent-serverapp-c121
                        id="betTime"
                        width="6%"
                        className="align-L"
                      >
                        {" "}
                        SportName{" "}
                      </th>
                      <th
                        _ngcontent-serverapp-c121
                        width="5%"
                        className="sort_asc"
                      >
                        Total Profit/loss
                      </th>
                      <th
                        _ngcontent-serverapp-c121
                        width="5%"
                        className="sort_asc"
                      >
                        Total Stake{" "}
                      </th>
                    </tr>

                    {gamePlRows.length > 0 ? (
                      gamePlRows.map((item, index) => {
                        const isOpen = expandedGame === item?.runnerName;
                        return (
                          <React.Fragment key={item?.runnerName || index}>
                            <tr>
                              <td className="align-L">
                                <a
                                  className={
                                    isOpen ? "expand-close" : "expand-open"
                                  }
                                  style={{ width: "15px", cursor: "pointer" }}
                                  onClick={() => toggleGameExpand(item)}
                                />
                                <a href="#" onClick={(e) => e.preventDefault()}>
                                  <span className="sprtcls">
                                    {item?.runnerName}
                                  </span>
                                </a>
                              </td>
                              <td className="align-r">
                                <div>
                                  <span className={plClass(item?.amount)}>
                                    {" "}
                                    {plLabel(item?.amount)}{" "}
                                  </span>
                                </div>
                              </td>
                              <td className="align-r">
                                {" "}
                                {toFixedWithoutRounding(
                                  Math.abs(item?.amount || 0),
                                )}
                              </td>
                            </tr>

                            {isOpen && (
                              <tr
                                className="expand"
                                style={{ display: "table-row" }}
                                id={`sub_${index}`}
                              >
                                <td colSpan={5}>
                                  <img
                                    src="/assets/images/transparent.gif"
                                    className="expand-arrow-R"
                                  />
                                  <table className="table-commission">
                                    <tbody>
                                      <tr>
                                        <th width="20%" className="align-L">
                                          GameName
                                        </th>
                                        <th width="12%">Total Profit/loss</th>
                                        <th width="23%">Total Stake</th>
                                      </tr>
                                      {gameDetails.length > 0 ? (
                                        gameDetails.map((res, i) => (
                                          <tr className="even" key={i}>
                                            <td className="align-L">
                                              <span className="sprtcls">
                                                {res?.matchName}
                                              </span>
                                            </td>
                                            <td id="totalPNL">
                                              <div>
                                                <span
                                                  className={plClass(
                                                    res?.amount,
                                                  )}
                                                >
                                                  {" "}
                                                  {plLabel(res?.amount)}{" "}
                                                </span>
                                              </div>
                                            </td>
                                            <td id="txLiability">
                                              {" "}
                                              {toFixedWithoutRounding(
                                                Math.abs(res?.amount || 0),
                                              )}{" "}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan={3}>No games found</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={3}>
                          You have no bets in this time period.
                        </td>
                      </tr>
                    )}

                    {gamePlRows.length > 0 && (
                      <tr className="total">
                        <td className="align-L">Total</td>
                        <td className="align-R">
                          <div>
                            <span className={plClass(gamePlTotal)}>
                              {" "}
                              {plLabel(gamePlTotal)}{" "}
                            </span>
                          </div>
                        </td>
                        <td className="align-R">
                          <div>
                            <span>
                              {toFixedWithoutRounding(gamePlStakeTotal)}{" "}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "casino" && (
          <div
            _ngcontent-serverapp-c121
            id="reportDiv"
            className="over-wrap"
            style={{ maxHeight: "calc(100% - 32px - 93px - 55px)" }}
          >
            <div
              _ngcontent-serverapp-c121
              className="table-responsive data-table"
            >
              <table
                ref={tableRef}
                _ngcontent-serverapp-c121
                id="table_DL"
                className="table01 table-pt"
              >
                <tbody _ngcontent-serverapp-c121>
                  <tr id="table_DL">
                    <th
                      id="header_userName"
                      width="15%"
                      className="align-L sort_asc"
                    >
                      UID
                    </th>
                    <th width="20%" className="sort_asc">
                      {" "}
                      Player P/L{" "}
                    </th>
                    <th
                      id="header_profitLoss_downLine"
                      width="13%"
                      className="sort_asc"
                    >
                      {" "}
                      Downline P/L{" "}
                    </th>
                    <th
                      id="header_tax_agent_2"
                      width="10%"
                      className="sort_asc"
                    >
                      Agent Comm.
                    </th>
                    <th width="15%" className="sort_asc">
                      {" "}
                      Upline P/L{" "}
                    </th>
                  </tr>
                </tbody>
                <tbody _ngcontent-serverapp-c121 id="content">
                  {casinoRows.length > 0 ? (
                    casinoRows.map((node) => renderCasinoNode(node, 0))
                  ) : (
                    <tr>
                      <td colSpan={5}>You have no bets in this time period.</td>
                    </tr>
                  )}

                  {casinoRows.length > 0 && (
                    <tr className="total">
                      <td className="align-L">Total</td>
                      <td>
                        <div>
                          <span className={plClass(casinoTotal)}>
                            {" "}
                            {plLabel(casinoTotal)}{" "}
                          </span>
                        </div>
                      </td>
                      <td id="_totalDownlinePl">
                        <span className={casinoTotal < 0 ? "red" : ""}>
                          {" "}
                          {plLabel(casinoTotal)}{" "}
                        </span>
                      </td>
                      <td id="_totalComm">
                        <span> {toFixedWithoutRounding(0)} </span>
                      </td>
                      <td id="_totalUplinePL">
                        <span>
                          {" "}
                          {toFixedWithoutRounding(Math.abs(casinoTotal))}{" "}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "playerPnl" && (
          <div
            _ngcontent-serverapp-c127
            id="reportDiv"
            className="over-wrap"
            style={{ maxHeight: "calc(100% - 32px - 93px - 55px)" }}
          >
            <div
              _ngcontent-serverapp-c127
              className="table-responsive data-table"
            >
              <table
                ref={tableRef}
                _ngcontent-serverapp-c127
                id="table_DL"
                className="table01 table-pt"
              >
                <tbody _ngcontent-serverapp-c127>
                  <tr id="table_DL">
                    <th
                      id="header_userName"
                      width="15%"
                      className="align-L sort_asc"
                    >
                      UID
                    </th>
                    <th width="20%" className="sort_asc">
                      Player P/L{" "}
                    </th>
                    <th
                      id="header_profitLoss_downLine"
                      width="13%"
                      className="sort_asc"
                    >
                      {" "}
                      Downline P/L{" "}
                    </th>
                    <th
                      id="header_tax_agent_2"
                      width="10%"
                      className="sort_asc"
                    >
                      {" "}
                      Agent Comm.
                    </th>
                    <th width="15%" className="sort_asc">
                      Upline P/L{" "}
                    </th>
                  </tr>
                </tbody>
                <tbody _ngcontent-serverapp-c127 id="content">
                  {playerRows.length > 0 ? (
                    playerRows.map((item, index) => (
                      <tr id="table_DL" key={item?._id || index}>
                        <td className="align-L">
                          <a id="_userName" className="ico_account">
                            <span className="Bdlevel premium" />
                            {item?.username}{" "}
                          </a>
                        </td>
                        <td id="_profitLoss">
                          <div>
                            <span className={plClass(item?.amount)}>
                              {" "}
                              {plLabel(item?.amount)}{" "}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span>{toFixedWithoutRounding(0)}</span>
                          </div>
                        </td>
                        <td id="_tax2">
                          <div>
                            <span>
                              {" "}
                              {toFixedWithoutRounding(
                                item?.commission || 0,
                              )}{" "}
                            </span>
                          </div>
                        </td>
                        <td id="_profitLossUpLine">
                          <div>
                            <span>
                              {" "}
                              {toFixedWithoutRounding(
                                Math.abs(item?.amount || 0),
                              )}{" "}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>You have no bets in this time period.</td>
                    </tr>
                  )}

                  {playerRows.length > 0 && (
                    <tr className="total">
                      <td className="align-L">Total</td>
                      <td>
                        <div>
                          <span className={plClass(playerTotal)}>
                            {" "}
                            {plLabel(playerTotal)}{" "}
                          </span>
                        </div>
                      </td>
                      <td id="_totalDownlinePl">
                        <span> {toFixedWithoutRounding(0)} </span>
                      </td>
                      <td id="_totalComm">
                        <span> {toFixedWithoutRounding(0)} </span>
                      </td>
                      <td id="_totalUplinePL">
                        <span>
                          {" "}
                          {toFixedWithoutRounding(Math.abs(playerTotal))}{" "}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </app-pl-awccasino>
  );
};

export default ReportByAWCcasino;
